import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { toast } from 'sonner';
import { Mail, Send, CalendarClock, AlertCircle, FileSpreadsheet, RefreshCw } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

export default function EmailTab() {
  const [testFollowupEmail, setTestFollowupEmail] = useState('vijay@mjenterprises.net');
  const [sendingFollowup, setSendingFollowup] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingOverdue, setSendingOverdue] = useState(false);
  const [sendingFinancial, setSendingFinancial] = useState(false);

  const sendTestFollowupEmail = async () => {
    if (!testFollowupEmail.trim()) { toast.error('Please enter an email address'); return; }
    setSendingFollowup(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email/test?to_email=${encodeURIComponent(testFollowupEmail.trim())}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Payment reminder test email sent to ${testFollowupEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingFollowup(false);
    }
  };

  const sendTestDailyDigest = async () => {
    setSendingDigest(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email/send-daily-digest`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Daily digest email sent to admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send daily digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const sendTestOverdueDigest = async () => {
    setSendingOverdue(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email/send-overdue-digest`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Overdue bills email sent to admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send overdue digest');
    } finally {
      setSendingOverdue(false);
    }
  };

  const sendTestFinancialDigest = async () => {
    setSendingFinancial(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email/send-financial-digest`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Financial summary report sent to admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send financial digest');
    } finally {
      setSendingFinancial(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Info card */}
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-xl">
            <Mail className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-1">Email Test Center</h2>
            <p className="text-sm text-slate-600">
              Send test emails to verify templates and delivery. All automated emails use the M&amp;J logo and branded templates.
            </p>
          </div>
        </div>
      </Card>

      {/* Payment Follow-up Test */}
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-amber-100 rounded-xl">
            <Send className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Payment Follow-up Reminder</h2>
            <p className="text-sm text-slate-500">Sends a sample payment reminder email with MJ branding</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1">
            <Label className="text-sm font-medium mb-2 block">Recipient Email</Label>
            <Input
              type="email"
              value={testFollowupEmail}
              onChange={e => setTestFollowupEmail(e.target.value)}
              placeholder="email@example.com"
              className="h-11 rounded-xl"
              data-testid="test-followup-email-input"
            />
          </div>
          <Button
            onClick={sendTestFollowupEmail}
            disabled={sendingFollowup}
            className="bg-amber-500 hover:bg-amber-600 text-white gap-2 h-11 px-6 whitespace-nowrap"
            data-testid="send-test-followup-btn"
          >
            {sendingFollowup ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Sending...</>
            ) : (
              <><Send className="w-4 h-4" /> Send Test</>
            )}
          </Button>
        </div>
      </Card>

      {/* Daily Digest Test */}
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-emerald-100 rounded-xl">
            <CalendarClock className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Daily Digest Email</h2>
            <p className="text-sm text-slate-500">
              Triggers the morning digest now — sent to the configured admin email.
              Auto-scheduled daily at <span className="font-medium text-slate-700">3:00 AM IST</span>.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full text-center text-sm">
            {[
              { label: 'New Expenses', color: 'text-amber-600' },
              { label: 'New Silica Loads', color: 'text-blue-600' },
              { label: 'Employee Changes', color: 'text-purple-600' },
              { label: 'Payments Received', color: 'text-emerald-600' },
            ].map(item => (
              <div key={item.label} className="flex flex-col items-center gap-1">
                <span className={`font-semibold text-xs ${item.color}`}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        <Button
          onClick={sendTestDailyDigest}
          disabled={sendingDigest}
          className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2 h-11 px-6"
          data-testid="send-test-digest-btn"
        >
          {sendingDigest ? (
            <><RefreshCw className="w-4 h-4 animate-spin" /> Sending...</>
          ) : (
            <><CalendarClock className="w-4 h-4" /> Send Daily Digest Now</>
          )}
        </Button>
      </Card>

      {/* Overdue Bills Digest */}
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-red-100 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Overdue &amp; Past Due Bills Email</h2>
            <p className="text-sm text-slate-500">
              Sends client-wise overdue and past due bill details — auto-scheduled at <span className="font-medium text-slate-700">3:00 AM IST</span>.
              If more than 30 records, remaining are attached as Excel.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-xl mb-5">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-yellow-100 border border-yellow-400 inline-block"></span>
            <span className="text-xs text-slate-600">Overdue</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-red-100 border border-red-300 inline-block"></span>
            <span className="text-xs text-slate-600">Past Due</span>
          </div>
          <span className="text-xs text-slate-500">Grouped by client · Max 30 in email · Rest in Excel</span>
        </div>
        <Button
          onClick={sendTestOverdueDigest}
          disabled={sendingOverdue}
          className="bg-red-500 hover:bg-red-600 text-white gap-2 h-11 px-6"
          data-testid="send-test-overdue-btn"
        >
          {sendingOverdue ? (
            <><RefreshCw className="w-4 h-4 animate-spin" /> Sending...</>
          ) : (
            <><AlertCircle className="w-4 h-4" /> Send Overdue Bills Now</>
          )}
        </Button>
      </Card>

      {/* Financial Summary Report */}
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-slate-100 rounded-xl">
            <FileSpreadsheet className="w-5 h-5 text-slate-700" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Financial Summary Report</h2>
            <p className="text-sm text-slate-500">
              Sends P&amp;L, account balances and silica usage summary — auto-scheduled at <span className="font-medium text-slate-700">3:00 AM IST</span>.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-slate-50 rounded-xl mb-5 text-xs text-slate-600">
          <div><span className="font-semibold text-blue-600 block mb-1">Financial Summary</span>Total Income, Expenses, Net Profit</div>
          <div><span className="font-semibold text-purple-600 block mb-1">Account Balances</span>Net Balance, +AR, +Inventory</div>
          <div><span className="font-semibold text-emerald-600 block mb-1">Silica Usage</span>Purchased, Sent &amp; Remaining Tons</div>
        </div>
        <Button
          onClick={sendTestFinancialDigest}
          disabled={sendingFinancial}
          className="bg-slate-700 hover:bg-slate-800 text-white gap-2 h-11 px-6"
          data-testid="send-test-financial-btn"
        >
          {sendingFinancial ? (
            <><RefreshCw className="w-4 h-4 animate-spin" /> Sending...</>
          ) : (
            <><FileSpreadsheet className="w-4 h-4" /> Send Financial Report Now</>
          )}
        </Button>
      </Card>
    </div>
  );
}
